import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Input, 
  useColorModeValue, 
  useToast,
  Container,
  Heading,
  IconButton,
  useBreakpointValue
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { processDonation } from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';

interface DonationOption {
  amount: number;
  tagline: string;
}

const Donation: React.FC = () => {
  const { creatorId, contentId } = useParams<{ creatorId: string; contentId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [creatorName, setCreatorName] = useState('');

  // Stripe Payment Links
  const STRIPE_LINK_CUSTOM = 'https://donate.stripe.com/aFa7sL4zGfI9az70iW5c400';
  const STRIPE_LINK_10 = 'https://donate.stripe.com/5kQ6oHgioanPbDbghU5c401';
  const STRIPE_LINK_25 = 'https://donate.stripe.com/6oU5kDd6c0Nf0YxfdQ5c402';
  const STRIPE_LINK_50 = 'https://donate.stripe.com/3cI00j2ry9jL7mVe9M5c403';
  const STRIPE_LINK_100 = 'https://donate.stripe.com/14AdR90jqgMd9v3c1E5c404';

  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const donationOptions: DonationOption[] = [
    { amount: 10, tagline: 'Empower change' },
    { amount: 25, tagline: 'Champion our mission' },
    { amount: 50, tagline: 'Make a real difference' },
    { amount: 100, tagline: 'Go big for impact' }
  ];

  useEffect(() => {
    // Get creator name from location state or set default
    const state = location.state as { creatorName?: string };
    setCreatorName(state?.creatorName || 'Creator');
  }, [location.state]);

  const handleDonationOptionClick = (amount: number) => {
    setSelectedAmount(amount);
  };

  const handleMakeDonation = async () => {
    if (!user) {
      toast({
        title: 'Please sign in',
        description: 'You need to be signed in to make a donation',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedAmount) {
      toast({
        title: 'Please select an amount',
        description: 'Please choose one of the donation amounts',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!creatorId) {
      toast({
        title: 'Error',
        description: 'Creator information not available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Redirect to fixed-amount Stripe Payment Links
      if (selectedAmount === 10) {
        window.location.href = STRIPE_LINK_10;
        return;
      }
      if (selectedAmount === 25) {
        window.location.href = STRIPE_LINK_25;
        return;
      }
      if (selectedAmount === 50) {
        window.location.href = STRIPE_LINK_50;
        return;
      }
      if (selectedAmount === 100) {
        window.location.href = STRIPE_LINK_100;
        return;
      }

      // Fallback to existing processing (should not occur with current preset options)
      await processDonation(user.uid, creatorId, selectedAmount, message, contentId);
      
      toast({
        title: 'Donation successful!',
        description: `Thank you for supporting ${creatorName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(-1);
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: 'Donation failed',
        description: 'Unable to process donation. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isDonationValid = selectedAmount;

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      {/* Header */}
      <Box 
        bg={bgColor} 
        borderBottom="1px" 
        borderColor={borderColor}
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="container.md" py={4}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <IconButton
                aria-label="Go back"
                icon={<ArrowBackIcon />}
                variant="ghost"
                onClick={handleBack}
                size={isMobile ? 'sm' : 'md'}
              />
              <Heading size={isMobile ? 'md' : 'lg'} color={textColor}>
                Support Creator
              </Heading>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.md" py={8}>
        <VStack gap={8} align="stretch">
          {/* Creator Info */}
          <VStack gap={2} textAlign="center">
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Support {creatorName}
            </Text>
            <Text fontSize="md" color="gray.600">
              Your support helps creators continue making amazing content
            </Text>
          </VStack>

          {/* Introduction */}
          <VStack gap={4} align="center" textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Together, We Can Make a Difference
            </Text>
            <Text fontSize="md" color="gray.600" lineHeight="1.6">
              Your support fuels our passion to help others, spread positivity, and create a better tomorrow.
            </Text>
          </VStack>

          {/* Donation Question */}
          <Text fontSize="xl" fontWeight="bold" color={textColor} textAlign="center">
            How much do you want to support?
          </Text>

          {/* Selected Amount Display */}
          {selectedAmount && (
            <Box
              p={6}
              bg="purple.100"
              borderRadius="xl"
              border="2px solid"
              borderColor="purple.300"
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold" color="purple.800">
                Selected Amount: ${selectedAmount}
              </Text>
              <Text fontSize="md" color="purple.600">
                Preset option selected
              </Text>
            </Box>
          )}

          {/* Preset Donation Options */}
          <VStack gap={4} align="stretch">
            {donationOptions.map((option) => (
              <Box
                key={option.amount}
                p={6}
                border="2px solid"
                borderColor={selectedAmount === option.amount ? 'purple.500' : borderColor}
                borderRadius="xl"
                cursor="pointer"
                bg={selectedAmount === option.amount ? 'purple.100' : 'transparent'}
                _hover={{ borderColor: 'purple.300', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
                onClick={() => handleDonationOptionClick(option.amount)}
                position="relative"
              >
                {selectedAmount === option.amount && (
                  <Box
                    position="absolute"
                    top={4}
                    right={4}
                    bg="purple.500"
                    color="white"
                    borderRadius="full"
                    w={8}
                    h={8}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    ✓
                  </Box>
                )}
                <VStack gap={2}>
                  <Text 
                    fontSize="3xl" 
                    fontWeight="bold" 
                    color={selectedAmount === option.amount ? 'purple.800' : textColor}
                  >
                    ${option.amount}
                  </Text>
                  <Text 
                    fontSize="md" 
                    color={selectedAmount === option.amount ? 'purple.600' : 'gray.600'} 
                    textAlign="center"
                  >
                    {option.tagline}
                  </Text>
                </VStack>
              </Box>
            ))}
          </VStack>


          {/* Message Input */}
          <VStack gap={3} align="stretch">
            <Text fontSize="lg" fontWeight="semibold" color={textColor}>
              Leave a message (optional)
            </Text>
            <Input
              placeholder="Say something nice to the creator..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              size="lg"
              borderColor={borderColor}
              _focus={{ borderColor: 'purple.500' }}
            />
          </VStack>

          {/* Action Buttons */}
          <VStack gap={4} pt={4}>
            <Button
              variant="outline"
              size="lg"
              w="100%"
              h="56px"
              onClick={() => { window.location.href = STRIPE_LINK_CUSTOM; }}
              isDisabled={isProcessing}
            >
              Custom Amount ($5–$500)
            </Button>
            <Button
              colorScheme="purple"
              size="lg"
              w="100%"
              h="60px"
              fontSize="xl"
              onClick={handleMakeDonation}
              isDisabled={!isDonationValid}
              isLoading={isProcessing}
              loadingText="Processing..."
            >
              Make Donation
            </Button>
            <Button
              variant="outline"
              size="lg"
              w="100%"
              h="50px"
              onClick={handleBack}
              isDisabled={isProcessing}
            >
              Cancel
            </Button>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default Donation;
